import {
  AppBar,
  makeStyles,
  Theme,
  Toolbar,
  Typography,
} from '@material-ui/core';
import {
  ADDITIONAL_BILLING_STATUS,
  getAdditionalBillingDisplayStatus,
} from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { EditableAdditionalBillingStatusSelect } from './EditableAdditionalBillingStatusSelect';

type Props = {
  flex?: number;
  loadStatus: string;
  sticky?: boolean;
  loadId?: number;
  status?: ADDITIONAL_BILLING_STATUS;
  billingIssueDescription?: string;
  onStatusUpdated?: () => void;
};

const getStatusColors = (status: string, theme: Theme) => {
  switch (status.toUpperCase()) {
    case 'PENDING':
      return { backgroundColor: '#f18e36', color: '#ffffff' };
    case 'DENIED':
      return { backgroundColor: theme.palette.error.main, color: '#ffffff' };
    case 'APPROVED':
      return { backgroundColor: '#10425a', color: '#ffffff' };
    case 'INVOICED':
      return { backgroundColor: '#9140dd', color: '#ffffff' };
    default:
      return { backgroundColor: theme.palette.error.main, color: '#ffffff' };
  }
};

const AdditionalBillingStatusAppBar: React.FC<Props> = props => {
  const classes = useStyles(props);
  const {
    flex,
    loadStatus,
    sticky,
    loadId,
    status,
    billingIssueDescription,
    onStatusUpdated,
  } = props;

  const { rootStore } = useStores();
  const user = rootStore.me?.maybeCurrent;
  const isDispatcher = !!user?.isDispatcher;
  const isBilling = !!user?.isBilling;
  const isInvoiced = status === ADDITIONAL_BILLING_STATUS.INVOICED;

  const isCanEdit =
    (isDispatcher || isBilling) && !!loadId && !!status && !isInvoiced;

  // Format Billing Issue with detail description if present
  let displayMessage = loadStatus;
  if (status === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE) {
    if (billingIssueDescription) {
      displayMessage = billingIssueDescription.includes(',')
        ? 'Billing Issues - Other'
        : `Billing Issue - ${billingIssueDescription}`;
    } else {
      displayMessage = getAdditionalBillingDisplayStatus(status);
    }
  }

  return (
    <AppBar
      position="static"
      className={[classes.appBar, sticky ? classes.sticky : ''].join(' ')}
      style={{ flex }}
    >
      <Toolbar className={classes.toolbar} variant="dense">
        {isCanEdit ? (
          <EditableAdditionalBillingStatusSelect
            loadId={loadId!}
            currentStatus={status!}
            displayMessage={displayMessage}
            billingIssueDescription={billingIssueDescription}
            isBillingUser={isBilling}
            onStatusUpdated={onStatusUpdated}
          />
        ) : (
          <Typography variant="h3" noWrap className={classes.text}>
            {displayMessage}
          </Typography>
        )}
      </Toolbar>
    </AppBar>
  );
};

const useStyles = makeStyles(theme => ({
  appBar: (props: Props) => ({
    ...getStatusColors(props.loadStatus, theme),
  }),
  sticky: { position: 'sticky', top: 0 },
  toolbar: {
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  text: { textAlign: 'center', textTransform: 'uppercase' },
}));

export default React.memo(observer(AdditionalBillingStatusAppBar));
